import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, Phone, MapPin, BadgeCheck, ClipboardList, ChevronDown, Sparkles } from "lucide-react";
import { MaskedLine } from "@/lib/Reveal";
import { COMPANY, IMAGES, PROJECTS, scrollToId } from "@/lib/site";

const SLIDES = [
    { src: IMAGES.hero, alt: "Modern kitchen renovation interior by a construction company in Luton" },
    { src: PROJECTS[2].img, alt: "Kitchen units being installed during a home renovation in Luton" },
    { src: PROJECTS[6].img, alt: "Loft extension construction on a house in Luton" },
    { src: PROJECTS[0].img, alt: "Painter refreshing interior walls in a Luton home" },
];

const ROTATING = ["Home", "Kitchen", "Loft", "Property"];

export default function KineticHero() {
    const ref = useRef(null);
    const [slide, setSlide] = useState(0);
    const [word, setWord] = useState(0);

    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const smx = useSpring(mx, { stiffness: 60, damping: 18 });
    const smy = useSpring(my, { stiffness: 60, damping: 18 });
    const cardX = useTransform(smx, [-1, 1], [-16, 16]);
    const cardY = useTransform(smy, [-1, 1], [-12, 12]);
    const glowX = useTransform(smx, [-1, 1], [-120, 120]);
    const glowY = useTransform(smy, [-1, 1], [-80, 80]);

    useEffect(() => {
        const t = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 5000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        const t = setInterval(() => setWord((w) => (w + 1) % ROTATING.length), 2600);
        return () => clearInterval(t);
    }, []);

    const onMouseMove = (e) => {
        const r = ref.current.getBoundingClientRect();
        mx.set(((e.clientX - r.left) / r.width) * 2 - 1);
        my.set(((e.clientY - r.top) / r.height) * 2 - 1);
    };

    return (
        <section
            id="home"
            ref={ref}
            data-testid="hero-section"
            onMouseMove={onMouseMove}
            className="relative min-h-screen flex flex-col justify-end bg-[#141414] overflow-hidden noise-overlay"
        >
            <div className="absolute inset-0">
                <AnimatePresence mode="sync">
                    <motion.img
                        key={slide}
                        src={SLIDES[slide].src}
                        alt={SLIDES[slide].alt}
                        initial={{ opacity: 0, scale: 1.08 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ opacity: { duration: 1.4, ease: "easeInOut" }, scale: { duration: 6.5, ease: "linear" } }}
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                </AnimatePresence>
                <div className="absolute inset-0 bg-[#141414]/70" />
                <div className="absolute inset-0 hero-grid-overlay" />
                <motion.div
                    style={{ x: glowX, y: glowY }}
                    className="absolute top-1/3 left-1/4 w-[480px] h-[480px] rounded-full bg-[#E85A1D]/25 blur-[140px] pointer-events-none"
                />
                <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-[#141414] to-transparent" />
            </div>

            <motion.div
                style={{ y: contentY, opacity: fade }}
                className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-36 pb-12"
            >
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.15, duration: 0.6 }}
                            className="flex items-center gap-3 mb-8"
                        >
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E85A1D] opacity-75" />
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#E85A1D]" />
                            </span>
                            <p className="font-mono-brand text-[11px] sm:text-xs uppercase tracking-[0.28em] text-white/70 font-semibold">
                                Construction · Maintenance · Home Improvement — Luton
                            </p>
                        </motion.div>

                        <h1
                            data-testid="hero-title-masked"
                            className="font-display font-extrabold uppercase text-white leading-[0.95] tracking-tight text-[13.5vw] sm:text-[10vw] lg:text-[7.2rem]"
                        >
                            <MaskedLine delay={0.25}>Smart</MaskedLine>
                            <MaskedLine delay={0.38}>
                                Solutions<span className="text-[#E85A1D]">.</span>
                            </MaskedLine>
                            <MaskedLine delay={0.51} className="text-white/90">
                                <span className="flex flex-wrap items-baseline gap-x-[0.35em]">
                                    For Your
                                    <span className="relative inline-block overflow-hidden align-baseline min-w-[4.2ch]">
                                        <AnimatePresence mode="wait">
                                            <motion.em
                                                key={word}
                                                initial={{ y: "105%" }}
                                                animate={{ y: 0 }}
                                                exit={{ y: "-105%" }}
                                                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                                                className="inline-block font-display italic font-extrabold text-[#E85A1D]"
                                            >
                                                {ROTATING[word]}
                                            </motion.em>
                                        </AnimatePresence>
                                    </span>
                                </span>
                            </MaskedLine>
                        </h1>

                        <motion.p
                            data-testid="hero-subtitle"
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.85, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-7 max-w-xl text-base sm:text-lg text-white/70 leading-relaxed"
                        >
                            Professional construction, maintenance and home improvement services across Luton and
                            surrounding areas.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-9 flex flex-wrap items-center gap-4"
                        >
                            <button
                                data-testid="hero-primary-cta-button"
                                onClick={() => scrollToId("contact")}
                                className="group flex items-center gap-3 bg-[#E85A1D] hover:bg-[#D24B10] text-white font-bold px-8 py-4 rounded-full text-base transition-colors duration-200 shadow-[0_12px_40px_rgba(232,90,29,0.35)]"
                            >
                                Get a Free Quote
                                <ArrowRight className="w-5 h-5 transition-transform duration-200 group-hover:translate-x-1" />
                            </button>
                            <button
                                data-testid="hero-secondary-cta-button"
                                onClick={() => scrollToId("services")}
                                className="flex items-center gap-3 border-2 border-white/30 hover:border-white text-white font-bold px-8 py-4 rounded-full text-base transition-colors duration-200 backdrop-blur-sm"
                            >
                                Our Services
                            </button>
                        </motion.div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.15, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                        className="hidden lg:block lg:col-span-4"
                    >
                        <motion.div
                            style={{ x: cardX, y: cardY }}
                            className="rounded-2xl border border-white/15 bg-white/[0.07] backdrop-blur-xl p-7 shadow-[0_24px_70px_rgba(0,0,0,0.4)]"
                        >
                            <div className="flex items-center gap-2.5">
                                <Sparkles className="w-4 h-4 text-[#E85A1D]" />
                                <p className="font-mono-brand text-[10px] uppercase tracking-[0.25em] text-white/60 font-semibold">
                                    Free Quote Today
                                </p>
                            </div>
                            <p className="mt-4 text-sm text-white/70 leading-relaxed">
                                Speak directly with the team — no call centres, no waiting lists.
                            </p>
                            <div className="mt-5 space-y-2.5">
                                {[
                                    { d: COMPANY.phoneDisplay, h: COMPANY.phoneHref },
                                    { d: COMPANY.phone2Display, h: COMPANY.phone2Href },
                                ].map((p) => (
                                    <a
                                        key={p.h}
                                        href={p.h}
                                        className="flex items-center gap-3 rounded-xl bg-white/[0.06] hover:bg-[#E85A1D] border border-white/10 hover:border-[#E85A1D] px-4 py-3 transition-colors duration-200 group"
                                    >
                                        <Phone className="w-4 h-4 text-[#E85A1D] group-hover:text-white transition-colors duration-200" />
                                        <span className="font-display font-bold text-white tracking-tight whitespace-nowrap">
                                            {p.d}
                                        </span>
                                    </a>
                                ))}
                            </div>
                            <div className="mt-5 flex items-center gap-2">
                                {SLIDES.map((_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setSlide(i)}
                                        aria-label={`Show background ${i + 1}`}
                                        className={`h-1.5 rounded-full transition-[width,background-color] duration-400 ${
                                            i === slide ? "w-8 bg-[#E85A1D]" : "w-3 bg-white/25 hover:bg-white/50"
                                        }`}
                                    />
                                ))}
                            </div>
                        </motion.div>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.3, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10 backdrop-blur-md"
                >
                    <div data-testid="hero-stat-badge-1" className="bg-[#141414]/60 px-6 py-5 flex items-center gap-4">
                        <MapPin className="w-6 h-6 text-[#E85A1D] shrink-0" />
                        <div>
                            <p className="font-display font-bold text-white">Luton Based</p>
                            <p className="text-xs text-white/55">Serving the town & surrounding areas</p>
                        </div>
                    </div>
                    <div data-testid="hero-stat-badge-2" className="bg-[#141414]/60 px-6 py-5 flex items-center gap-4">
                        <ClipboardList className="w-6 h-6 text-[#E85A1D] shrink-0" />
                        <div>
                            <p className="font-display font-bold text-white">Free Quotes</p>
                            <p className="text-xs text-white/55">Clear pricing, no obligation</p>
                        </div>
                    </div>
                    <div data-testid="hero-stat-badge-3" className="bg-[#141414]/60 px-6 py-5 flex items-center gap-4">
                        <BadgeCheck className="w-6 h-6 text-[#E85A1D] shrink-0" />
                        <div>
                            <p className="font-display font-bold text-white">7 Core Services</p>
                            <p className="text-xs text-white/55">One trusted local team</p>
                        </div>
                    </div>
                </motion.div>
            </motion.div>

            <motion.button
                onClick={() => scrollToId("about")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.8, duration: 0.8 }}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-1.5 text-white/50 hover:text-white transition-colors duration-200"
                aria-label="Scroll down to learn more"
            >
                <motion.span
                    animate={{ y: [0, 7, 0] }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                >
                    <ChevronDown className="w-5 h-5" />
                </motion.span>
            </motion.button>
        </section>
    );
}
